import React, { useState, useEffect } from 'react';
import { FaUserEdit, FaTimes } from 'react-icons/fa';
import { EntradaAfiliadoDto } from '../types';

interface ModalNominacionProps {
  entrada: EntradaAfiliadoDto | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (idEntradaDetalle: number, nombre: string, apellido: string, dni: string, email: string) => Promise<void>;
  cargando: boolean;
}

export const ModalNominacion: React.FC<ModalNominacionProps> = ({
  entrada,
  isOpen,
  onClose,
  onSave,
  cargando
}) => {
  const [nombre, setNombre] = useState('');
  const [apellido, setApellido] = useState('');
  const [dni, setDni] = useState('');
  const [email, setEmail] = useState('');
  const [errorLocal, setErrorLocal] = useState<string | null>(null);

  useEffect(() => {
    if (entrada) {
      if (entrada.portadorNombre) {
        const partes = entrada.portadorNombre.split(' ');
        setNombre(partes[0] || '');
        setApellido(partes.slice(1).join(' ') || '');
      } else {
        setNombre('');
        setApellido('');
      }
      setDni(entrada.portadorDni || '');
      setEmail(entrada.portadorEmail || '');
      setErrorLocal(null);
    }
  }, [entrada]);

  if (!isOpen || !entrada) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nombre.trim() || !apellido.trim() || !dni.trim() || !email.trim()) {
      setErrorLocal('Todos los campos son obligatorios.');
      return;
    }

    try {
      setErrorLocal(null);
      await onSave(entrada.idEntradaDetalle, nombre.trim(), apellido.trim(), dni.trim(), email.trim());
      onClose();
    } catch (err: any) {
      setErrorLocal(err.mensaje || 'Error al procesar la nominación.');
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[999999]"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-boxdark rounded-2xl max-w-lg w-full shadow-2xl border border-slate-100 dark:border-strokedark overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header con insignia de alto contraste */}
        <div className="bg-primario-azul-900 px-6 py-4 flex items-center justify-between text-white">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white shadow-sm flex-shrink-0">
              <FaUserEdit className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-base text-white">
                {entrada.nominada ? 'Reasignar Portador de Entrada' : 'Nominar Entrada'}
              </h3>
              <p className="text-xs text-blue-200">Ticket #{entrada.numeroEntrada}</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            aria-label="Cerrar modal"
            className="p-1.5 rounded-full hover:bg-white/20 text-white transition duration-150"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {errorLocal && (
            <div className="p-3 bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-700/50 rounded-xl text-rose-700 dark:text-rose-300 text-xs">
              ⚠️ {errorLocal}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="nombre-input" className="block text-xs font-bold text-slate-700 dark:text-slate-200 mb-1">Nombre</label>
              <input
                id="nombre-input"
                type="text"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-meta-4 border border-slate-200 dark:border-strokedark rounded-xl text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-primario-celeste-800"
                placeholder="Ej: Juan"
                required
              />
            </div>
            <div>
              <label htmlFor="apellido-input" className="block text-xs font-bold text-slate-700 dark:text-slate-200 mb-1">Apellido</label>
              <input
                id="apellido-input"
                type="text"
                value={apellido}
                onChange={(e) => setApellido(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-meta-4 border border-slate-200 dark:border-strokedark rounded-xl text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-primario-celeste-800"
                placeholder="Ej: Pérez"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="dni-input" className="block text-xs font-bold text-slate-700 dark:text-slate-200 mb-1">DNI / Documento</label>
            <input
              id="dni-input"
              type="text"
              value={dni}
              onChange={(e) => setDni(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-meta-4 border border-slate-200 dark:border-strokedark rounded-xl text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-primario-celeste-800"
              placeholder="Ej: 30123456"
              required
            />
          </div>

          <div>
            <label htmlFor="email-input" className="block text-xs font-bold text-slate-700 dark:text-slate-200 mb-1">Email del Portador</label>
            <input
              id="email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-meta-4 border border-slate-200 dark:border-strokedark rounded-xl text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-primario-celeste-800"
              placeholder="Ej: juan.perez@email.com"
              required
            />
            <p className="text-[11px] text-slate-400 dark:text-slate-400 mt-1">Se enviará una copia del comprobante y código QR a esta casilla.</p>
          </div>

          <div className="pt-4 border-t border-slate-100 dark:border-strokedark flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={cargando}
              className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition duration-150"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={cargando}
              className="px-5 py-2 text-xs font-bold text-white bg-primario-celeste-800 hover:bg-primario-azul-900 rounded-xl transition duration-150 flex items-center space-x-2"
            >
              {cargando ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Guardando...</span>
                </>
              ) : (
                <span>Guardar Nominación</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
